/**
 * District BUILDINGS: the nine tinted models on their plots, their shared
 * colormap-atlas masks (wall-only tint, window emissive), the day/dusk switch,
 * the back-of-house massing behind each model, and the bay reveals for any
 * plot without a model. Extracted from districtScene so building look changes
 * live in one place.
 */
import * as pc from 'playcanvas'
import { registerCollider } from '../colliders'
import { box,
  WINDOW_EMISSIVE, WINDOW_EMISSIVE_INTENSITY } from './districtShared'
import { getCachedAsset, getPropEntry, onAssetReady } from '../assets'
import type { AssetZoneController } from '../assetZones'
import {
  BUILDING_NATURAL_SIZE,
  plotFootprint, plotFrontZ, bayCentres, buildingPlacement,
  plotColliderBoxes, GROUND_FLOOR_H, type PlotDef,
} from './district'
import { interiorFor } from './interiors'

// ── Shared colormap atlas ────────────────────────────────────────────────────
// The nine building GLBs share one 512x512 `colormap` atlas with an identical
// UV layout (verified by decoding the Draco meshes): the wall swatch sits at
// u∈[32,64] px and the glass/window swatch at u∈[96,128] px. That shared layout
// is exactly what lets a single 1-D column mask address a feature across every
// building — a wall-only tint, or one emissive map that lights every window.
const ATLAS_W = 512
const WINDOW_U0 = 96
const WINDOW_U1 = 128

/** A 512x1 column texture painted by `paint` — clamped, so it reads as columns. */
function columnMask(app: pc.Application, paint: (ctx: CanvasRenderingContext2D) => void): pc.Texture {
  const canvas = document.createElement('canvas')
  canvas.width = ATLAS_W
  canvas.height = 1
  const ctx = canvas.getContext('2d')
  const tex = new pc.Texture(app.graphicsDevice, {
    width: ATLAS_W, height: 1,
    addressU: pc.ADDRESS_CLAMP_TO_EDGE,
    addressV: pc.ADDRESS_CLAMP_TO_EDGE,
  })
  if (ctx) { paint(ctx); tex.setSource(canvas) }
  return tex
}

/**
 * White at the window swatch, black everywhere else. Built ONCE and shared as
 * the emissive map of every building, so a single texture lights the whole
 * district's windows — and, being an emissive map, it is inert wherever the
 * mask is black and while the intensity is zero (day).
 */
export function windowEmissiveMask(app: pc.Application): pc.Texture {
  return columnMask(app, (ctx) => {
    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, ATLAS_W, 1)
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(WINDOW_U0, 0, WINDOW_U1 - WINDOW_U0, 1)
  })
}

/**
 * The plot tint over the wall/roof swatches, white (multiply identity) from the
 * window swatch onward. Used as a MUL detail map so the tint darkens ONLY the
 * wall — the dark glass, the white trim and the awning keep full value instead
 * of being dragged toward black with the rest of the atlas.
 */
function wallTintMask(app: pc.Application, tint: readonly [number, number, number]): pc.Texture {
  const r = Math.round(tint[0] * 255)
  const g = Math.round(tint[1] * 255)
  const b = Math.round(tint[2] * 255)
  return columnMask(app, (ctx) => {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, ATLAS_W, 1)
    ctx.fillStyle = `rgb(${r},${g},${b})`
    // Everything left of the window swatch is wall and roof.
    ctx.fillRect(0, 0, WINDOW_U0, 1)
  })
}

/**
 * Places a plot's building model, scaled onto its frontage.
 *
 * `instantiateRenderEntity` hands back clones that SHARE the source material,
 * so touching one would touch every building in the district. Each gets its own
 * material clone — which is also the whole point: these Kenney models carry a
 * single material over one atlas, so per-clone tint + gloss + emissive turn one
 * mesh into a distinct building for almost nothing. The tint goes through a
 * wall-only detail mask (not the base diffuse) so the glass keeps its value.
 */
export function attachBuildingModel(
  app: pc.Application,
  parent: pc.Entity,
  plot: PlotDef,
  winMask: pc.Texture,
  assetZones?: AssetZoneController<pc.Asset>,
): void {
  const placement = buildingPlacement(plot)
  if (!placement || !plot.model) return

  const holder = new pc.Entity(`plot-${plot.id}-building`)
  holder.setLocalScale(placement.scale[0], placement.scale[1], placement.scale[2])
  holder.setPosition(placement.position[0], placement.position[1], placement.position[2])
  // These models draw their shopfront — glazing, awnings, doors — on their
  // -Z face; +Z is a blank wall. This rotation was inverted, so every building
  // in the district showed the street its back and buried the detail 15 m into
  // the plot. The placement owns the district-facing yaw plus any cataloged
  // correction needed by a corner-origin or side-authored model.
  holder.setEulerAngles(0, placement.yaw, 0)
  parent.addChild(holder)

  const attach = (asset: pc.Asset): () => void => {
    if (!asset.resource) throw new Error(`Asset ${plot.model} has no container resource`)
    const container = asset.resource as pc.ContainerResource
    const entity = container.instantiateRenderEntity({ castShadows: true })
    holder.addChild(entity)

    // Licensed Synty landmarks carry their authored atlas material. The
    // Kenney-only wall/window masks below address a different UV layout and
    // would recolour roofs and signs if applied here.
    if (plot.model?.startsWith('synty_')) return () => entity.destroy()
    if (!plot.tint) return () => entity.destroy()
    // One tint mask per building — the material is shared across this model's
    // meshes, so it is built once and re-pointed below.
    const tintMask = wallTintMask(app, plot.tint)
    // Build the source -> clone map FIRST, then re-point once. Re-pointing
    // inside the discovery loop meant the next iteration saw its own clone as
    // a new source and cloned again: N clones and O(N^2) traversals per
    // building, each surviving clone costing a shader variant.
    const renders = entity.findComponents('render') as pc.RenderComponent[]
    const clones = new Map<pc.Material, pc.StandardMaterial>()
    for (const render of renders) {
      for (const mi of render.meshInstances) {
        const source = mi.material
        if (!(source instanceof pc.StandardMaterial)) continue
        if (clones.has(source)) continue
        const cloned = source.clone()
        // Tint the WALL only: keep the base diffuse neutral and multiply the
        // wall swatch through a detail map, so the glass/trim/awning survive.
        cloned.diffuse = new pc.Color(1, 1, 1)
        cloned.diffuseDetailMap = tintMask
        cloned.diffuseDetailMapChannel = 'rgb'
        cloned.diffuseDetailMapUv = 0
        cloned.diffuseDetailMode = pc.DETAILMODE_MUL
        // Material family — gloss and metalness carry as much identity as hue.
        cloned.useMetalness = true
        cloned.metalness = plot.metalness ?? 0
        cloned.gloss = plot.gloss ?? 0.2
        // Lit windows: one shared emissive mask, warm, off (intensity 0) by day.
        cloned.emissiveMap = winMask
        cloned.emissiveMapChannel = 'rgb'
        cloned.emissive = WINDOW_EMISSIVE.clone()
        cloned.emissiveIntensity = WINDOW_EMISSIVE_INTENSITY
        cloned.update()
        clones.set(source, cloned)
      }
    }
    for (const render of renders) {
      for (const mi of render.meshInstances) {
        const cloned = clones.get(mi.material)
        if (cloned) mi.material = cloned
      }
    }
    return () => entity.destroy()
  }

  const runtime = getPropEntry(plot.model)?.runtime
  if (assetZones && runtime?.loadClass === 'zone') {
    assetZones.register(runtime.zone, plot.model, attach)
  } else {
    const cached = getCachedAsset(plot.model)
    if (cached) attach(cached)
    else onAssetReady(plot.model, attach)
  }
}

/**
 * Back-of-house massing for whatever plot depth the model doesn't occupy.
 * Real deep plots look exactly like this: the building is the frontage, and
 * the rest is storage nobody ever sees the inside of.
 */
export function buildMassing(
  parent: pc.Entity,
  plot: PlotDef,
  massMat: pc.Material,
  groundMat: pc.Material,
): void {
  const f = plotFootprint(plot)
  const placement = buildingPlacement(plot)

  if (placement && placement.backDepth > 0.5 && plot.proceduralBackfill !== false) {
    const w = f.maxX - f.minX
    const d = placement.backDepth
    const cx = (f.minX + f.maxX) / 2
    // Behind the model, away from the street.
    const cz = plot.side === 'north'
      ? plotFrontZ(plot) - placement.modelDepth - d / 2
      : plotFrontZ(plot) + placement.modelDepth + d / 2
    // Back-of-house must never overtop the licensed frontage. The old plot
    // envelope could hide a low maker hall or mechanical shell behind a plain
    // procedural wall, defeating the silhouette the licensed model provides.
    const modelHeight = plot.model
      ? BUILDING_NATURAL_SIZE[plot.model][1] * placement.scale[1]
      : plot.height
    const backH = Math.max(
      GROUND_FLOOR_H,
      Math.min(plot.height * 0.55, modelHeight),
    )
    box(parent, `plot-${plot.id}-back`, [cx, backH / 2, cz], [w, backH, d], massMat)
  } else if (!placement) {
    // No model for this plot — fall back to grey massing so the street still
    // has a wall rather than a hole.
    const w = f.maxX - f.minX
    const d = f.maxZ - f.minZ
    const cx = (f.minX + f.maxX) / 2
    const cz = (f.minZ + f.maxZ) / 2
    const groundH = Math.min(GROUND_FLOOR_H, plot.height)
    box(parent, `plot-${plot.id}-ground`, [cx, groundH / 2, cz], [w, groundH, d], groundMat)
    const upperH = plot.height - groundH
    if (upperH > 0.01) {
      box(parent, `plot-${plot.id}-upper`,
        [cx, groundH + upperH / 2, cz], [w, upperH, d], massMat)
    }
  }

  // Solid everywhere except the doorway. A single box for the whole footprint
  // is what sealed the office inside the anchor plot.
  for (const b of plotColliderBoxes(plot, interiorFor(plot.id) !== null)) registerCollider(b)
}

/**
 * A shallow reveal per bay on the street face. Costs almost nothing and is
 * what stops a long frontage reading as one blank slab at walking speed —
 * the vertical rhythm is doing the work here, not the material.
 */
export function buildBayReveals(
  parent: pc.Entity,
  plot: PlotDef,
  mat: pc.Material,
): void {
  const frontZ = plotFrontZ(plot)
  const outward = plot.side === 'north' ? 0.08 : -0.08
  for (const cx of bayCentres(plot)) {
    box(parent, `plot-${plot.id}-pier-${cx.toFixed(1)}`,
      [cx, GROUND_FLOOR_H / 2, frontZ + outward],
      [0.35, GROUND_FLOOR_H, 0.16], mat)
  }
}

/** Canvas-drawn fascia sign — zero download, and it can say anything. */
/**
 * The entrance: a recessed opening, a frame, a canopy over it, and a lit
 * fascia sign. Signage sits inside the band a walking player can actually see
 * — from across the street the camera only takes in the lowest few metres of
 * any facade, so a sign up at cornice height is invisible where it matters.
 */
